import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Users,
  Shield,
  Download,
  Sparkles,
  Check,
  ChevronRight,
  ChevronLeft,
  Mail,
  Plus,
  Trash2,
  Crown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 'company_info', title: 'Company Info', icon: Building2 },
  { id: 'plan_selection', title: 'Select Plan', icon: Crown },
  { id: 'team_invites', title: 'Invite Team', icon: Users },
  { id: 'security_prefs', title: 'Security', icon: Shield },
  { id: 'install_agent', title: 'Install Agent', icon: Download },
  { id: 'dashboard_tour', title: 'Get Started', icon: Sparkles },
];

const plans = [
  {
    id: 'trial',
    name: 'Free Trial',
    price: 0,
    period: '14 days',
    features: ['5 users', '10 devices', 'Basic protection', 'Email support'],
    recommended: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 299,
    period: '/month',
    features: ['10 users', '50 devices', 'Advanced protection', 'AI threat analysis', 'Priority support'],
    recommended: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 599,
    period: '/month',
    features: ['15 users', 'Unlimited devices', 'Full protection suite', 'Custom integrations', 'Dedicated support'],
    recommended: false,
  },
];

const industries = [
  'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education', 'Government', 'Other'
];

const companySizes = [
  '1-10', '11-50', '51-200', '201-500', '500+'
];

export default function DirectClientOnboarding() {
  const navigate = useNavigate();
  const { setAuthToken } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  
  // Check for token from signup flow and URL step parameter
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    const onboardingToken = sessionStorage.getItem('onboarding_token');
    
    if (onboardingToken) {
      // User came from signup, set the token and start at the specified step
      setToken(onboardingToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${onboardingToken}`;
      
      if (stepParam) {
        setCurrentStep(parseInt(stepParam, 10));
      }
      
      // Clear the token from sessionStorage after using it
      sessionStorage.removeItem('onboarding_token');
    }
  }, []);
  
  // Form data
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    industry: 'Technology',
    company_size: '1-10',
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });
  
  const [selectedPlan, setSelectedPlan] = useState('trial');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const [teamInvites, setTeamInvites] = useState([
    { email: '', name: '', role: 'analyst' }
  ]);
  
  const [securityPrefs, setSecurityPrefs] = useState({
    block_malicious_domains: true,
    block_newly_registered: true,
    auto_quarantine_files: true,
    scan_email_attachments: true,
    critical_alerts_enabled: true,
  });

  const handleStartOnboarding = async () => {
    if (!companyInfo.company_name || !companyInfo.admin_email || !companyInfo.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/onboarding/start`, companyInfo);
      setToken(response.data.access_token);
      
      // Store token for subsequent requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      setCurrentStep(1);
      toast.success('Account created! Let\'s continue setup.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/plan`, { plan: selectedPlan });
      setCurrentStep(2);
    } catch (error) {
      toast.error('Failed to select plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvites = async () => {
    setLoading(true);
    try {
      const validInvites = teamInvites.filter(i => i.email);
      await axios.post(`${API}/api/onboarding/team-invites`, { invites: validInvites });
      setCurrentStep(3);
      if (validInvites.length > 0) {
        toast.success(`${validInvites.length} invite(s) sent!`);
      }
    } catch (error) {
      toast.error('Failed to send invites');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSecurityPrefs = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/security-prefs`, securityPrefs);
      setCurrentStep(4);
    } catch (error) {
      toast.error('Failed to save security preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleAgentDownload = async (platform) => {
    // Simulate download
    toast.success(`Downloading ${platform} agent...`);
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/agent-downloaded`);
      setCurrentStep(5);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, { tour_completed: true });
      
      // Set token using AuthContext method
      if (token) {
        setAuthToken(token);
      }
      
      toast.success('Welcome to Clear Defense AI!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipStep = async () => {
    const stepId = steps[currentStep].id;
    
    try {
      await axios.post(`${API}/api/onboarding/skip-step`, { step: stepId });
      
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleComplete();
      }
    } catch (error) {
      console.error(error);
      setCurrentStep(currentStep + 1);
    }
  };

  const addInvite = () => {
    setTeamInvites([...teamInvites, { email: '', name: '', role: 'analyst' }]);
  };

  const removeInvite = (index) => {
    setTeamInvites(teamInvites.filter((_, i) => i !== index));
  };

  const updateInvite = (index, field, value) => {
    const updated = [...teamInvites];
    updated[index][field] = value;
    setTeamInvites(updated);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Welcome to Clear Defense AI</h2>
              <p className="text-slate-400 mt-2">Let&apos;s get your organization protected in minutes</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Company Name *</Label>
                <Input
                  value={companyInfo.company_name}
                  onChange={(e) => setCompanyInfo({...companyInfo, company_name: e.target.value})}
                  placeholder="Acme Corp"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Industry</Label>
                <Select value={companyInfo.industry} onValueChange={(v) => setCompanyInfo({...companyInfo, industry: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Company Size</Label>
                <Select value={companyInfo.company_size} onValueChange={(v) => setCompanyInfo({...companyInfo, company_size: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {companySizes.map(s => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Your Name *</Label>
                <Input
                  value={companyInfo.admin_name}
                  onChange={(e) => setCompanyInfo({...companyInfo, admin_name: e.target.value})}
                  placeholder="John Doe"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={companyInfo.admin_email}
                  onChange={(e) => setCompanyInfo({...companyInfo, admin_email: e.target.value})}
                  placeholder="john@acme.com"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  value={companyInfo.admin_password}
                  onChange={(e) => setCompanyInfo({...companyInfo, admin_password: e.target.value})}
                  placeholder="Min. 8 characters"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked)}
                className="mt-1 border-slate-600 data-[state=checked]:bg-blue-600"
              />
              <label htmlFor="terms" className="text-xs text-slate-400 cursor-pointer">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-blue-400 hover:text-blue-300 underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-blue-400 hover:text-blue-300 underline">
                  Privacy Policy
                </a>
              </label>
            </div>
            
            <Button onClick={handleStartOnboarding} disabled={loading || !agreedToTerms} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? 'Creating Account...' : 'Continue'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Choose Your Plan</h2>
              <p className="text-slate-400 mt-2">Start with a free trial, upgrade anytime</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <Card
                  key={plan.id}
                  className={`cursor-pointer transition-all ${
                    selectedPlan === plan.id
                      ? 'bg-blue-500/10 border-blue-500'
                      : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <CardContent className="p-6">
                    {plan.recommended && (
                      <Badge className="bg-blue-500 text-white mb-3">Recommended</Badge>
                    )}
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-white">${plan.price}</span>
                      <span className="text-slate-400">{plan.period}</span>
                    </div>
                    <ul className="mt-4 space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {selectedPlan === plan.id && (
                      <div className="mt-4 flex justify-center">
                        <Check className="w-6 h-6 text-blue-500" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(0)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleSelectPlan} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Invite Your Team</h2>
              <p className="text-slate-400 mt-2">Add team members to help manage security (optional)</p>
            </div>
            
            <div className="space-y-4">
              {teamInvites.map((invite, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1 space-y-2">
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      type="email"
                      value={invite.email}
                      onChange={(e) => updateInvite(index, 'email', e.target.value)}
                      placeholder="colleague@company.com"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label className="text-slate-300">Name</Label>
                    <Input
                      value={invite.name}
                      onChange={(e) => updateInvite(index, 'name', e.target.value)}
                      placeholder="Jane Smith"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="w-32 space-y-2">
                    <Label className="text-slate-300">Role</Label>
                    <Select value={invite.role} onValueChange={(v) => updateInvite(index, 'role', v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="analyst">Analyst</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {teamInvites.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeInvite(index)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button variant="outline" onClick={addInvite} className="border-slate-700 w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Another
              </Button>
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(1)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleSkipStep} className="text-slate-400">
                  Skip for now
                </Button>
                <Button onClick={handleSendInvites} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Sending...' : teamInvites.some(i => i.email) ? 'Send Invites' : 'Continue'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Security Preferences</h2>
              <p className="text-slate-400 mt-2">Configure your protection settings</p>
            </div>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Block Malicious Domains</p>
                    <p className="text-sm text-slate-400">Automatically block known malicious websites</p>
                  </div>
                  <Switch
                    checked={securityPrefs.block_malicious_domains}
                    onCheckedChange={(v) => setSecurityPrefs({...securityPrefs, block_malicious_domains: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Block Newly Registered Domains</p>
                    <p className="text-sm text-slate-400">Block domains registered in the last 30 days</p>
                  </div>
                  <Switch
                    checked={securityPrefs.block_newly_registered}
                    onCheckedChange={(v) => setSecurityPrefs({...securityPrefs, block_newly_registered: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-Quarantine Suspicious Files</p>
                    <p className="text-sm text-slate-400">Automatically quarantine high-risk files</p>
                  </div>
                  <Switch
                    checked={securityPrefs.auto_quarantine_files}
                    onCheckedChange={(v) => setSecurityPrefs({...securityPrefs, auto_quarantine_files: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Scan Email Attachments</p>
                    <p className="text-sm text-slate-400">Scan all email attachments for threats</p>
                  </div>
                  <Switch
                    checked={securityPrefs.scan_email_attachments}
                    onCheckedChange={(v) => setSecurityPrefs({...securityPrefs, scan_email_attachments: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Critical Threat Alerts</p>
                    <p className="text-sm text-slate-400">Get immediate alerts for critical threats</p>
                  </div>
                  <Switch
                    checked={securityPrefs.critical_alerts_enabled}
                    onCheckedChange={(v) => setSecurityPrefs({...securityPrefs, critical_alerts_enabled: v})}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(2)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleSaveSecurityPrefs} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Install the Agent</h2>
              <p className="text-slate-400 mt-2">Download and install Clear Defense AI on your devices</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['Windows', 'macOS', 'Linux'].map((platform) => (
                <Card key={platform} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-lg bg-slate-800 flex items-center justify-center mx-auto mb-4">
                      <Download className="w-8 h-8 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">{platform}</h3>
                    <Button
                      variant="outline"
                      className="border-slate-700 w-full"
                      onClick={() => handleAgentDownload(platform.toLowerCase())}
                    >
                      Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <p className="text-center text-slate-500 text-sm">
              You can always download agents later from the Agent Download page
            </p>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(3)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={handleSkipStep} className="text-slate-400">
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white">You&apos;re All Set!</h2>
              <p className="text-slate-400 mt-2">Your organization is now protected by Clear Defense AI</p>
            </div>
            
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-white mb-4">What&apos;s Next?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="text-white">Explore your dashboard</p>
                      <p className="text-sm text-slate-400">View real-time threat monitoring and analytics</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="text-white">Install agents on more devices</p>
                      <p className="text-sm text-slate-400">Extend protection to all your endpoints</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="text-white">Configure automation rules</p>
                      <p className="text-sm text-slate-400">Set up automated responses to threats</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Button onClick={handleComplete} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? 'Loading...' : 'Go to Dashboard'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isCompleted ? 'bg-emerald-500' :
                      isCurrent ? 'bg-blue-500' :
                      'bg-slate-800'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isCurrent ? 'text-white' : 'text-slate-500'}`} />
                      )}
                    </div>
                    <span className={`mt-2 text-xs ${isCurrent ? 'text-white' : 'text-slate-500'}`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      index < currentStep ? 'bg-emerald-500' : 'bg-slate-800'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
